import { type ReactNode } from 'react';
import { Check, Info, X } from 'lucide-react';
import { ColumnDef } from '@/components';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

const ICON_COLUMN_HEADER_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center whitespace-normal leading-[1.15] align-middle';
const ICON_COLUMN_CELL_CLASS =
	'w-[5.75rem] min-w-[5.75rem] max-w-[5.75rem] px-1.5 text-center cursor-default';

const STAFF_COLUMN_HEADER_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2';
const STAFF_COLUMN_CELL_CLASS =
	'w-[9.5rem] min-w-[9.5rem] max-w-[9.5rem] px-2 cursor-text';

const TEXT_COLUMN_HEADER_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2';
const TEXT_COLUMN_CELL_CLASS =
	'w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2 cursor-text';

const IconColumnCell = ({ children }: { children: ReactNode }) => (
	<div className="flex w-full items-center justify-center">{children}</div>
);

const resolveStaffInitial = (row: Admins.Admin) => {
	const source =
		row.name?.trim()?.charAt(0) || row.email?.trim()?.charAt(0) || '?';
	return source.charAt(0).toUpperCase();
};

const StaffAvatar = ({ row }: { row: Admins.Admin }) => {
	const initial = resolveStaffInitial(row);

	return (
		<div
			className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-bold uppercase text-zinc-600 ring-1 ring-zinc-200/80"
			aria-hidden
		>
			{initial}
		</div>
	);
};

const CheckStatusBadge = ({
	enabled,
	label,
}: {
	enabled: boolean;
	label: string;
}) => (
	<span
		className={cn(
			'inline-flex size-7 cursor-help items-center justify-center rounded-full',
			enabled
				? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100'
				: 'bg-stone-100 text-stone-300 ring-1 ring-stone-200/80',
		)}
		aria-label={label}
		title={label}
	>
		{enabled ? (
			<Check className="size-3.5" strokeWidth={2.75} aria-hidden />
		) : (
			<X className="size-3.5" strokeWidth={2.75} aria-hidden />
		)}
	</span>
);

const ColumnHeaderWithHint = ({
	label,
	hint,
}: {
	label: string;
	hint: string;
}) => (
	<span className="inline-flex max-w-full flex-col items-center justify-center gap-0.5">
		<span className="inline-flex items-center justify-center gap-0.5">
			<span className="text-center">{label}</span>
			<Tooltip content={hint} position="top">
				<button
					type="button"
					className="inline-flex shrink-0 cursor-help rounded-full text-stone-400 transition-colors hover:text-stone-600"
					aria-label={hint}
				>
					<Info
						className="size-3 shrink-0"
						strokeWidth={2.25}
						aria-hidden
					/>
				</button>
			</Tooltip>
		</span>
	</span>
);

const formatDate = (value?: string | null) => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleDateString('pt-BR');
};

const StaffColumns = (): ColumnDef<Admins.Admin, keyof Admins.Admin>[] => [
	{
		header: (
			<ColumnHeaderWithHint
				label="Ativo"
				hint="Conta ativa no painel administrativo"
			/>
		),
		accessor: 'active',
		headerClassName: ICON_COLUMN_HEADER_CLASS,
		cellClassName: ICON_COLUMN_CELL_CLASS,
		cell: (value) => (
			<IconColumnCell>
				<CheckStatusBadge
					enabled={value === true}
					label={value === true ? 'Conta ativa' : 'Conta inativa'}
				/>
			</IconColumnCell>
		),
	},
	{
		header: 'Conta',
		accessor: 'name',
		headerClassName: STAFF_COLUMN_HEADER_CLASS,
		cellClassName: STAFF_COLUMN_CELL_CLASS,
		cell: (_, row) => {
			const name = row.name?.trim() || '—';
			const email = row.email?.trim() || '—';

			return (
				<div className="flex min-w-0 items-center gap-2">
					<StaffAvatar row={row} />
					<div className="min-w-0 truncate">
						<p
							className="truncate text-xs font-medium text-dark"
							title={name}
						>
							{name}
						</p>
						<p
							className="truncate text-[11px] text-gray-500"
							title={email}
						>
							{email}
						</p>
					</div>
				</div>
			);
		},
	},
	{
		header: 'Cadastro',
		accessor: 'created_at',
		headerClassName: TEXT_COLUMN_HEADER_CLASS,
		cellClassName: TEXT_COLUMN_CELL_CLASS,
		cell: (value) => (
			<span className="block truncate text-xs text-dark">
				{formatDate(value as string | undefined)}
			</span>
		),
	},
];

export { StaffColumns };
